/**
 * Step 1: Context & Voorbereiding
 *
 * Shows employee info, self-evaluation summary, and previous evaluation.
 * Also includes the AI Evaluation Assistant for analyzing conversation notes.
 */

import { useEffect } from 'react';
import { toast } from 'sonner';
import { useEvaluation } from '../useEvaluations';
import { useWizard } from './WizardContext';
import { ScoreDisplay } from './ScoreInput';
import { AIEvaluationAssistant } from './AIAssistant';

interface Step1ContextProps {
  evaluationId: number;
}

export function Step1Context({ evaluationId }: Step1ContextProps) {
  const { data: evaluation, isLoading } = useEvaluation(evaluationId);
  const { updateStepData, stepData, nextStep } = useWizard();

  // Mark step as viewed
  useEffect(() => {
    if (!stepData.step1?.viewed) {
      updateStepData('step1', { viewed: true });
    }
  }, [updateStepData, stepData.step1?.viewed]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Data laden...</span>
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-700">Beoordeling niet gevonden.</p>
      </div>
    );
  }

  const hasSelfEvaluation = evaluation.selfEvaluations && evaluation.selfEvaluations.length > 0;
  const selfEvalAvg = hasSelfEvaluation
    ? evaluation.selfEvaluations.reduce((sum, se) => sum + se.score, 0) / evaluation.selfEvaluations.length
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">Stap 1: Context & Voorbereiding</h2>
        <p className="mt-1 text-sm text-gray-500">
          Bekijk de informatie van de medewerker en eventuele zelf-evaluatie voordat je begint.
        </p>
      </div>

      {/* Employee Info Card */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Medewerker Informatie</h3>
        </div>
        <div className="px-6 py-4">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <span className="text-blue-600 font-bold text-2xl">
                {evaluation.employee?.functie?.charAt(0) || 'M'}
              </span>
            </div>
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Functie</label>
                <p className="text-gray-900 font-medium">{evaluation.employee?.functie || 'Niet opgegeven'}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Team</label>
                <p className="text-gray-900">{evaluation.employee?.team || 'Niet opgegeven'}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Periode</label>
                <p className="text-gray-900">
                  {new Date(evaluation.periodeStart).toLocaleDateString('nl-NL')} -{' '}
                  {new Date(evaluation.periodeEnd).toLocaleDateString('nl-NL')}
                </p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Status</label>
                <p className="text-gray-900">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    evaluation.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                    evaluation.status === 'final' ? 'bg-green-100 text-green-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {evaluation.status === 'draft' ? 'Concept' :
                     evaluation.status === 'submitted' ? 'Ingediend' :
                     evaluation.status === 'in_review' ? 'In review' :
                     evaluation.status === 'final' ? 'Definitief' :
                     evaluation.status}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Self-Evaluation Summary */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Zelf-evaluatie Medewerker</h3>
        </div>
        <div className="px-6 py-4">
          {hasSelfEvaluation ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div>
                  <p className="text-sm text-blue-700">Gemiddelde zelf-score</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {selfEvalAvg?.toFixed(1)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-blue-700">Ingediend op</p>
                  <p className="text-gray-900">
                    {evaluation.selfEvaluationAt
                      ? new Date(evaluation.selfEvaluationAt).toLocaleDateString('nl-NL')
                      : 'Niet ingediend'}
                  </p>
                </div>
              </div>

              {/* Task scores table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taak</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Zelf-score</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Toelichting</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {evaluation.selfEvaluations.map((se, index) => (
                      <tr key={se.taskId || index}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Taak #{se.taskId}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <ScoreDisplay score={se.score} size="sm" />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {se.toelichting || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="mt-2 text-sm text-gray-500">
                De medewerker heeft nog geen zelf-evaluatie ingevuld.
              </p>
              <p className="mt-1 text-xs text-gray-400">
                Je kunt wel alvast starten met de beoordeling.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* AI Evaluation Assistant */}
      <AIEvaluationAssistant
        evaluationId={evaluationId}
        onApplied={() => {
          toast.success('AI suggesties zijn toegepast. Controleer de scores in de volgende stappen.');
        }}
      />

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-800">Tip voor de beoordeling</h4>
            <p className="mt-1 text-sm text-blue-700">
              Gebruik de zelf-evaluatie als startpunt voor het gesprek. Bespreek afwijkingen
              tussen de zelf-score en jouw beoordeling om tot een gedragen eindoordeel te komen.
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-end pt-4 border-t border-gray-200">
        <button
          onClick={nextStep}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Volgende: Taken Beoordelen
        </button>
      </div>
    </div>
  );
}
